// Exam Prep (JEE/NEET) — a premium gold ACCENT tuned per student theme,
// deliberately NOT a single fixed value: a flat gold read as a clash (pale
// cream badge, or a gold picked for light mode) against several of the 20+
// STUDENT_THEMES, especially the dark/heroic ones. Every theme id below
// gets its own gold, chosen against that theme's own bg/accent/mood —
// several themes (professional tier, several heroic ones) already use a
// gold-ish accent natively, so those are reused/derived directly rather
// than fighting a second, competing gold.
//
// The navy→charcoal hero surface stays fixed across every theme on
// purpose — that's the structural "this is the premium section" signature;
// only the gold accent adapts.

import { themeToCssVars, surfaceToAppTokens, readableTextOn } from './themes';

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const num = parseInt(full.slice(0, 6), 16) || 0;
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

function withAlpha(hex, alpha) {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function darken(hex, t) {
  const [r, g, b] = hexToRgb(hex);
  const d = (v) => Math.max(0, Math.round(v * (1 - t)));
  return `#${[d(r), d(g), d(b)].map(v => v.toString(16).padStart(2, '0')).join('')}`;
}

const THEME_GOLD = {
  // ── standard ──
  'deskey-classic': '#B8873D',
  'slate-pro':      '#AD8642',
  'graphite':       '#C2790A', // theme's own accent (#F59E0B) is already amber — deepened for text/icon use

  // ── editorial ──
  'espresso':       '#9C6B2E',
  'lavender-pro':   '#B8873D',
  'forest-pro':     '#B07A08', // near theme's own accent (#CA8A04)
  'mint-mocha':     '#A9691C',

  // ── vibrant ──
  'sunset':         '#D9A62B', // near theme's own accent (#FFD166)
  'ocean':          '#C08A2E',
  'pastel-pop':     '#C99A3D',

  // ── dark — brighter/more luminous so gold actually pops on near-black ──
  'midnight':       '#E8B94D',
  'noir':           '#D4A017', // classic film-noir gold, distinct from the theme's own yellow primary
  'cyberpunk':      '#F0B429',

  // ── playful (kids) ──
  'bubblegum':      '#E0AA1E', // near theme's own accent (#FFD93D)
  'crayon-box':     '#D99A3D',
  'jungle':         '#D9900C', // near theme's own accent (#F59E0B)
  'space-cadet':    '#C99A3D',
  'candy-pop':      '#E0A31C', // near theme's own accent (#FBBF24)
  'rainbow':        '#D98A1F',
  'story-time':     '#8F5A0F',

  // ── professional — these themes already carry a collegiate gold/bronze
  //    accent, so reuse or lightly tune it rather than adding a second one ──
  'oxford-navy':     '#C9A227', // = theme's own accent
  'harvard-crimson': '#B8860B', // dark goldenrod — crimson + gold, classic collegiate pairing
  'cambridge-stone': '#8B6914', // = theme's own accent
  'yale-royal':      '#D4AF37', // = theme's own accent
  'princeton-tiger': '#C77D0A',

  // ── heroic — several already run a gold/amber accent; dark ones get a
  //    brighter gold the same way the "dark" tier does ──
  'crimson-knight':   '#E0B000', // near theme's own accent (#F2C200)
  'web-indigo':       '#E8B94D',
  'star-patriot':     '#C08A2E',
  'royal-vibranium':  '#E0B300', // near theme's own accent (#FFD60A)
  'gamma-pulse':      '#E8B94D',
  'asgard-storm':     '#C28A2D', // = theme's own accent
  'merc-chaos':       '#B8873D',
  'shadow-vigilante': '#E0B000', // near theme's own accent (#FACC15)
};

const DEFAULT_GOLD = THEME_GOLD['deskey-classic'];

export const examPrep = {
  navy: '#1A365D',
  charcoal: '#101826',
};

export const examPrepSurfaceGradient = `linear-gradient(135deg, ${examPrep.navy}, ${examPrep.charcoal})`;

/** The per-theme premium accent — call with the active theme's id (from
 * useStudent().theme.id). Falls back to the flagship gold for an unknown/
 * missing id. */
export function getExamPrepColors(themeId) {
  const gold = THEME_GOLD[themeId] || DEFAULT_GOLD;
  return {
    gold,
    goldDeep: darken(gold, 0.22),
    goldTint: withAlpha(gold, 0.16),
  };
}

export function getExamPrepGoldGradient(themeId) {
  const c = getExamPrepColors(themeId);
  return `linear-gradient(135deg, ${c.gold}, ${c.goldDeep})`;
}

// ─── Exam Mode (tutor chat) ────────────────────────────────────────────────
// LIGHT themes: one fixed premium surface for all of them (a per-theme gold
// on top of each theme's own primary/fonts left bubbles, tabs and headings
// changing colour and typeface on every theme switch).
// DARK themes: the panel keeps the theme's own colours and only adds the
// exam gold + fixed typography (a fixed dark surface clashed with warm or
// coloured dark themes). Mode comes from theme.dark.

// `text` = gold used AS text/icons on the panel's own surfaces (labels,
// chips); `onGold` = text sitting ON a gold fill (toggle, send button).
const buildExamModeColors = (gold, text) => ({
  gold,
  goldDeep: darken(gold, 0.22),
  goldTint: withAlpha(gold, 0.16),
  text,
  onGold: examPrep.charcoal,
});

// Light: flagship gold; labels in deep gold (5.6:1 on white-ish surfaces).
const EXAM_MODE_COLORS_LIGHT = buildExamModeColors(THEME_GOLD['deskey-classic'], darken(THEME_GOLD['deskey-classic'], 0.22));
// Dark: the luminous gold the dark themes use -- labels in that same bright
// gold read 8.9:1 on the dark card; deep gold would sink into it.
const EXAM_MODE_COLORS_DARK = buildExamModeColors(THEME_GOLD.midnight, THEME_GOLD.midnight);

export const getExamModeColors = (dark) => (dark ? EXAM_MODE_COLORS_DARK : EXAM_MODE_COLORS_LIGHT);

export const getExamModeGoldGradient = (dark) => {
  const c = getExamModeColors(dark);
  return `linear-gradient(135deg, ${c.gold}, ${c.goldDeep})`;
};

// Light Exam Mode's student bubbles + active tab (white text on
// --st-primary): deep gold, #906930 is 4.94:1 with white (WCAG AA). Not the
// Exam Prep navy -- navy reads as "blue" and looked like the theme's colour
// on blue themes.
const EXAM_MODE_BUBBLE = EXAM_MODE_COLORS_LIGHT.goldDeep;

// Light Exam Mode: one fixed surface for every light theme.
const EXAM_MODE_LIGHT_SURFACE = {
  primary: EXAM_MODE_BUBBLE, accent: EXAM_MODE_COLORS_LIGHT.gold,
  bg: '#FAF8F3', card: '#FFFFFF', ink: '#111827', mute: '#6B7280', border: '#E7E0D0',
  tier: 'standard', headingFont: 'Inter', bodyFont: 'Inter',
};

// Spread onto the chat panel's style while Exam Mode is on. The student's
// own text-size setting (--st-font-scale) is deliberately left to inherit
// -- that's an accessibility preference, not part of the theme's look.
const { '--st-font-scale': _inheritedFontScale, ...lightVars } = themeToCssVars(EXAM_MODE_LIGHT_SURFACE);

const EXAM_MODE_LIGHT_PANEL_VARS = {
  ...lightVars,
  // The app-wide shadcn tokens too (index.css is light-only), so headings
  // and shared components match this panel, not the page around it.
  ...surfaceToAppTokens(EXAM_MODE_LIGHT_SURFACE),
  colorScheme: 'light',
};

// Dark Exam Mode keeps the THEME's own colours -- primary, secondary/accent,
// surfaces, text (and a student's custom accent override) -- because a fixed
// dark surface clashed with warm/coloured dark themes. Only the exam gold
// (cards, toggle, send, sparkles) and the fixed typography stay Exam Mode's.
// So: don't override any colour var here; the panel inherits the shell's.
const THEME_COLOUR_VARS = new Set([
  '--st-primary', '--st-accent', '--st-bg', '--st-card', '--st-ink', '--st-mute', '--st-border',
]);
const EXAM_MODE_TYPOGRAPHY_VARS = Object.fromEntries(
  Object.entries(lightVars).filter(([key]) => !THEME_COLOUR_VARS.has(key)),
);

/** Panel CSS vars for Exam Mode. `primaryHex` is the theme's effective
 *  primary (its accent_color override if set) -- dark mode uses it to pick
 *  readable bubble/tab text, since bright dark-theme primaries (yellow,
 *  cyan, white) make hardcoded white text unreadable. */
export const getExamModePanelVars = (dark, primaryHex) => (dark
  ? { ...EXAM_MODE_TYPOGRAPHY_VARS, '--st-on-primary': readableTextOn(primaryHex || '#000000'), colorScheme: 'dark' }
  : EXAM_MODE_LIGHT_PANEL_VARS);
